import { z } from "zod";

/**
 * A timezone the runtime knows by its IANA name. Engines list different spellings of the
 * same zone (Europe/Kyiv vs Europe/Kiev), so a name also counts when it resolves to a
 * listed one.
 */
export function isTimeZone(value: string): boolean {
  if (value === "UTC") return true;
  if (!/^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)+$/.test(value)) return false;
  const known = Intl.supportedValuesOf("timeZone");
  if (known.includes(value)) return true;
  try {
    return known.includes(
      new Intl.DateTimeFormat("en", { timeZone: value }).resolvedOptions().timeZone,
    );
  } catch {
    return false;
  }
}

export const TimeZone = z.string().max(64).refine(isTimeZone, "Unknown timezone");

export const WeekStart = z.enum(["monday", "sunday"]);
export type WeekStart = z.infer<typeof WeekStart>;

export const CalendarView = z.enum(["month", "week", "day"]);
export type CalendarView = z.infer<typeof CalendarView>;

/** Austrian states as ISO 3166-2 codes, for school holidays. */
export const SchoolHolidayRegion = z.enum([
  "AT-1",
  "AT-2",
  "AT-3",
  "AT-4",
  "AT-5",
  "AT-6",
  "AT-7",
  "AT-8",
  "AT-9",
]);
export type SchoolHolidayRegion = z.infer<typeof SchoolHolidayRegion>;

export const schoolHolidayRegionNames: Record<SchoolHolidayRegion, string> = {
  "AT-1": "Burgenland",
  "AT-2": "Kärnten",
  "AT-3": "Niederösterreich",
  "AT-4": "Oberösterreich",
  "AT-5": "Salzburg",
  "AT-6": "Steiermark",
  "AT-7": "Tirol",
  "AT-8": "Vorarlberg",
  "AT-9": "Wien",
};

/** spec10 FR2: reminder offsets in minutes before the due time or start; null = off. */
export const REMINDER_MINUTES = [0, 5, 10, 15, 30, 60] as const;
export const ReminderMinutes = z.literal(REMINDER_MINUTES);
export type ReminderMinutes = z.infer<typeof ReminderMinutes>;

/** A local time of day, `HH:MM` (24 h). */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected HH:MM");

/** spec13 FR2: pen sizes in page units (the page is 1000 wide) a user can pick. */
export const STROKE_SIZE_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 16, 20] as const;
export const StrokeSize = z.literal(STROKE_SIZE_OPTIONS);
export type StrokeSize = z.infer<typeof StrokeSize>;

export const Settings = z.object({
  timezone: TimeZone,
  weekStart: WeekStart,
  calendarDefaultView: CalendarView,
  showPublicHolidays: z.boolean(),
  /** null = no school holidays. */
  schoolHolidayRegion: SchoolHolidayRegion.nullable(),
  /** spec10 FR2: reminders through ntfy. */
  notificationsEnabled: z.boolean(),
  todoReminderMinutes: ReminderMinutes.nullable(),
  eventReminderMinutes: ReminderMinutes.nullable(),
  /** For todos without a time and all-day events; null = off. */
  dailyReminderTime: TimeOfDay.nullable(),
  /** spec13 FR2: the canvas pen sizes for the width keys 1, 2 and 3. */
  canvasStrokeThin: StrokeSize,
  canvasStrokeMedium: StrokeSize,
  canvasStrokeThick: StrokeSize,
  /** Read-only: whether the server has ntfy configured (spec10 FR1). */
  notificationsAvailable: z.boolean(),
});
export type Settings = z.infer<typeof Settings>;

/** What a user without stored settings gets (spec02 FR4). */
export const DEFAULT_SETTINGS: Settings = {
  timezone: "Europe/Vienna",
  weekStart: "monday",
  calendarDefaultView: "month",
  showPublicHolidays: true,
  schoolHolidayRegion: null,
  notificationsEnabled: false,
  todoReminderMinutes: 0,
  eventReminderMinutes: 15,
  dailyReminderTime: "08:00",
  canvasStrokeThin: 1.5,
  canvasStrokeMedium: 3,
  canvasStrokeThick: 5,
  notificationsAvailable: false,
};

export const UpdateSettings = Settings.omit({ notificationsAvailable: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: "Nothing to update",
  });
export type UpdateSettings = z.infer<typeof UpdateSettings>;
